/**
 * [O365-I 2026-10-04] Wspólne drobiazgi sekcji „Reguły incydentów” (/admin/integrations).
 *
 * Daty: serwer oddaje DZIEŃ jako `yyyy-MM-dd` (bez strefy), a chwilę jako ISO UTC z „Z”. Ekran pokazuje wszystko w czasie polskim
 * (Europe/Warsaw) — „dziś” do granicy daty poinformowania też liczymy w Polsce, tak samo jak serwer.
 */

/** Komunikat błędu serwera DOSŁOWNIE (`{ message }`), a gdy go nie ma — zdanie zapasowe. Nic z serwera nie idzie jako HTML. */
export const komunikatBledu = (err, zapasowy) => {
    const m = err?.response?.data?.message;
    return typeof m === "string" && m.trim() ? m : zapasowy;
};

/** Dzisiejsza data w Polsce jako `yyyy-MM-dd`. */
export const dzisWPolsce = (teraz = new Date()) => {
    try {
        // Szwedzki zapis daty to dokładnie ISO `yyyy-MM-dd`.
        return new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Warsaw" }).format(teraz);
    } catch {
        const d = teraz;
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    }
};

/** „15 października 2026” z `yyyy-MM-dd` (bez przesunięcia strefy: dzień jest dniem). */
export const formatujDzien = (dzien) => {
    if (!dzien || typeof dzien !== "string") return "";
    const [r, m, d] = dzien.split("-").map((x) => Number(x));
    if (!r || !m || !d) return dzien;
    const data = new Date(Date.UTC(r, m - 1, d, 12));
    return data.toLocaleDateString("pl-PL", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
};

/** „4 października 2026, 12:00” z chwili ISO UTC — w czasie polskim. */
export const formatujChwile = (iso) => {
    if (!iso) return "";
    const data = new Date(iso);
    if (Number.isNaN(data.getTime())) return "";
    return data.toLocaleString("pl-PL", {
        day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Warsaw",
    });
};

/** Nazwy poziomów reguł (plakietka i jej podpowiedź). */
export const ETYKIETY_POZIOMU = {
    K: "Krytyczny — natychmiast",
    W: "Wysoki — w kilka godzin",
    D: "Do przeglądu dziennego",
    T: "Do przeglądu tygodniowego",
};

/**
 * Zdarzenie okna z mostu SignalR (NotificationContext, „O365RegulySpolekZmiana” → „o365-reguly-spolek-zmiana”, ładunek
 * `{ companyId, rodzaj: "reguly" }`). Serwer wysyła je administratorom z dostępem do integracji — czyli dokładnie tym, którzy widzą
 * tę sekcję. Sygnału modułu („o365bezp-zmiana”: incydenty, alerty, zdrowie, konta) sekcja NIE słucha: nie dotyczy jej treści,
 * a ranga administratora go nie dostaje.
 */
export const ZDARZENIE_REGUL_SPOLEK = "o365-reguly-spolek-zmiana";

/** Rodzaj sygnału, na który sekcja reaguje (inne rodzaje — gdyby kiedyś doszły — nie każą pobierać listy). */
export const RODZAJ_REGULY = "reguly";
/** [2026-10-05] Rodzaj sygnału „zmienił się zapisany stan zgód aplikacji spółki” — blok zgód tej spółki pobiera stan ponownie. */
export const RODZAJ_ZGODY = "zgody";

/** Przedrostek parametru `state` strony zgody administratora Microsoft (to samo co serwer). */
export const PREFIKS_STANU_ZGODY = "o365zgody.";
/** Pamięć KARTY przeglądarki: zgoda w toku ({ state, companyId, rejestracja }) i wynik powrotu czekający na ekran. */
export const KLUCZ_ZGODY_W_TOKU = "o365.zgody.wToku";
export const KLUCZ_ZGODY_POWROT = "o365.zgody.powrot";

export const czytajSesje = (klucz) => {
    try {
        const tekst = window.sessionStorage.getItem(klucz);
        return tekst ? JSON.parse(tekst) : null;
    } catch {
        return null;
    }
};
export const zapiszSesje = (klucz, wartosc) => {
    try {
        if (wartosc == null) window.sessionStorage.removeItem(klucz);
        else window.sessionStorage.setItem(klucz, JSON.stringify(wartosc));
        return true;
    } catch {
        return false;
    }
};

/** Jak długo wynik powrotu ze strony zgody czeka w pamięci karty na ekran (potem jest pomijany — nie pokazujemy „po powrocie
 *  z Microsoftu” przy wejściu godzinę później). */
export const WAZNOSC_POWROTU_MS = 15 * 60 * 1000;

/** Czy zapamiętany wynik powrotu jest jeszcze świeży (ma czas zapisu i nie minął kwadrans). */
export const powrotSwiezy = (czeka, teraz = Date.now()) => {
    const kiedy = Number(czeka?.kiedy || 0);
    return kiedy > 0 && teraz - kiedy <= WAZNOSC_POWROTU_MS;
};

/** Adres główny aplikacji widziany przez przeglądarkę — to on musi stać w rejestracji aplikacji jako adres przekierowania
 *  (Microsoft porównuje go co do znaku). */
export const adresGlownyAplikacji = () => {
    try {
        return window.location.origin || "";
    } catch {
        return "";
    }
};

/** Przejście pod adres zewnętrzny w TYM samym oknie (osobny obiekt, żeby test mógł je podmienić). */
export const nawigacja = {
    przejdz: (adres) => window.location.assign(adres),
};

/** „przed chwilą”, „1 min temu”, „12 min temu”, „2 godz. temu” — od chwili ISO do `teraz` (ms). */
export const ileTemu = (iso, teraz = Date.now()) => {
    const t = iso ? new Date(iso).getTime() : NaN;
    if (Number.isNaN(t)) return "";
    const minuty = Math.floor(Math.max(0, teraz - t) / 60000);
    if (minuty < 1) return "przed chwilą";
    if (minuty < 60) return `${minuty} min temu`;
    const godziny = Math.floor(minuty / 60);
    if (godziny < 48) return `${godziny} godz. temu`;
    return `${Math.floor(godziny / 24)} dni temu`;
};

/** Kod odmowy serwera: konto bez indywidualnego PIN-u administratora (żaden PIN tego nie otworzy). */
export const KOD_BRAK_PINU = "admin_pin_individual_required";

/** `yyyy-MM-dd` przesunięte o podaną liczbę dni (dzień jest dniem — bez stref). Zły zapis → "". */
export const dodajDni = (dzien, ile) => {
    if (!dzien || typeof dzien !== "string") return "";
    const [r, m, d] = dzien.split("-").map((x) => Number(x));
    if (!r || !m || !d) return "";
    return new Date(Date.UTC(r, m - 1, d + ile)).toISOString().slice(0, 10);
};

/** Ile dni po poinformowaniu pracowników otwiera się bramka art. 22³ KP (to samo co serwer). */
export const DNI_PO_POINFORMOWANIU = 14;

/** „Spółka A”, „Spółka A i Spółka B”, „Spółka A, Spółka B i Spółka C” — z listy `{ nazwa }`. */
export const wymienSpolki = (spolki) => {
    const nazwy = (Array.isArray(spolki) ? spolki : []).map((x) => x?.nazwa).filter((n) => typeof n === "string" && n);
    if (nazwy.length <= 1) return nazwy.join("");
    return `${nazwy.slice(0, -1).join(", ")} i ${nazwy[nazwy.length - 1]}`;
};

/** Odbicie odświeżenia po sygnale na żywo (kilka sygnałów naraz = jedno pobranie). */
export const ODBICIE_MS = 400;
